import { Switch } from '@headlessui/react'
import { ArcElement, Chart as ChartJS, ChartOptions as ChartJSOptions, DoughnutController, Legend, Tooltip } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { motion } from 'framer-motion'
import { PieChartIcon as ChartPie, CurrencyIcon as CurrencyDollar, LineChartIcon, Loader2 } from 'lucide-react'
import React from 'react'
import { Pie } from 'react-chartjs-2'
import { useNavigate } from 'react-router-dom'
import AnimatedBackground from '../../utils/AnimatedBackground'
import Footer from '../../utils/Footer'
import TopBar from '../../utils/TopBar'
import { useInvestmentDashboard } from '../hooks/DashInvestments'
import { AssetTotalDash, ChartContext, ChartData, ChartOptions } from '../models/ModelInvestments'

const InvestmentDashboard: React.FC = () => {
  const { assetTotals, showUSD, setShowUSD, exchangeRate, isLoading, isRecalculating } = useInvestmentDashboard()
  const navigate = useNavigate()

  const formatCurrency = (value: string, currency: string) => {
    const numValue = parseFloat(value);
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (currency.toLowerCase() === 'brl' && showUSD) {
      return `$ ${formatter.format(numValue / exchangeRate)}`;
    } else if (currency.toLowerCase() === 'usd' && !showUSD) {
      return `R$ ${formatter.format(numValue * exchangeRate)}`;
    }
    return currency.toLowerCase() === 'brl' ? `R$ ${formatter.format(numValue)}` : `$ ${formatter.format(numValue)}`;
  };

  const calculateTotal = () => {
    const total = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_value);
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate);
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate);
      }
      return acc + (asset.currency.toLowerCase() === 'brl' ? value : value);
    }, 0);

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(total)}`;
  };

  const calculateTotalCurrentValue = () => {
    const total = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_current_value);
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate);
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate);
      }
      return acc + (asset.currency.toLowerCase() === 'brl' ? value : value);
    }, 0);

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(total)}`;
  };

  const calculateTotalProfit = () => {
    return assetTotals.reduce((acc, asset) => {
      const currentValue = parseFloat(asset.total_current_value);
      const investedValue = parseFloat(asset.total_value);
      const profit = currentValue - investedValue;
      
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (profit / exchangeRate);
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (profit * exchangeRate);
      }
      return acc + profit;
    }, 0);
  };

  const formatTotalProfit = () => {
    const profit = calculateTotalProfit();
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(profit)}`;
  };

  const calculateTotalDividends = () => {
    const total = assetTotals.reduce((acc, asset) => {
      if (!asset.total_dividends) return acc;
      
      const dividends = parseFloat(asset.total_dividends);
      if (isNaN(dividends)) return acc;
      
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (dividends / exchangeRate);
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (dividends * exchangeRate);
      }
      return acc + dividends;
    }, 0);

    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return `${showUSD ? '$ ' : 'R$ '}${formatter.format(total)}`;
  };

  const calculateTotalReturn = () => {
    const totalInvested = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_value);
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate);
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate);
      }
      return acc + value;
    }, 0);
  
    const totalProfit = calculateTotalProfit();
    const totalDividends = assetTotals.reduce((acc, asset) => {
      if (asset.asset_type === 'digital-coin' || !asset.total_dividends) {
        return acc;
      }
      const value = parseFloat(asset.total_dividends || '0');
      if (asset.currency.toLowerCase() === 'brl' && showUSD) {
        return acc + (value / exchangeRate);
      } else if (asset.currency.toLowerCase() === 'usd' && !showUSD) {
        return acc + (value * exchangeRate);
      }
      return acc + value;
    }, 0);
  
    return ((totalInvested + totalProfit + totalDividends) / totalInvested - 1) * 100;
  };

  const generateChartData = (type: 'asset' | 'currency') => {
    const totals = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_value);
      const adjustedValue = asset.currency.toLowerCase() === 'brl' && showUSD 
        ? value / exchangeRate 
        : asset.currency.toLowerCase() === 'usd' && !showUSD 
          ? value * exchangeRate 
          : value;

      const key = type === 'asset' ? asset.asset_type : asset.currency.toUpperCase();
      acc[key] = (acc[key] || 0) + adjustedValue;
      return acc;
    }, {} as Record<string, number>);

    // Calculate total for percentages
    const total = Object.values(totals).reduce((sum, value) => sum + value, 0);
    
    // Convert values to percentages
    const percentages = Object.entries(totals).map(([key, value]) => ({
      key,
      percentage: (value / total) * 100
    }));

    return {
      labels: percentages.map(item => 
        item.key.replace('-', ' ').toUpperCase()
      ),
      datasets: [{
        data: percentages.map(item => item.percentage),
        backgroundColor: [
          'rgba(59, 130, 246, 0.9)',   // Blue
          'rgba(239, 68, 68, 0.9)',    // Red
          'rgba(16, 185, 129, 0.9)',   // Green
          'rgba(245, 158, 11, 0.9)',   // Yellow
          'rgba(99, 102, 241, 0.9)',   // Indigo
        ],
        borderWidth: 2,
        borderColor: 'rgba(17, 24, 39, 0.8)'
      }]
    };
  };

  const generateLiquidityChartData = () => {
    const liquidityMap = {
      'stock': 'short-term',
      'digital-coin': 'short-term', 
      'tesouro-direto': 'long-term',
      'undefined': 'short-term'
    };

    const totals = assetTotals.reduce((acc, asset) => {
      const value = parseFloat(asset.total_value);
      const adjustedValue = asset.currency.toLowerCase() === 'brl' && showUSD 
        ? value / exchangeRate 
        : asset.currency.toLowerCase() === 'usd' && !showUSD 
          ? value * exchangeRate 
          : value;

      const liquidity = liquidityMap[asset.asset_type as keyof typeof liquidityMap] || 'short-term';
      acc[liquidity] = (acc[liquidity] || 0) + adjustedValue;
      return acc;
    }, {} as Record<string, number>);

    const total = Object.values(totals).reduce((sum, value) => sum + value, 0);
    const percentages = Object.entries(totals).map(([key, value]) => ({
      key,
      percentage: (value / total) * 100
    }));

    return {
      labels: percentages.map(item => 
        item.key.replace('-', ' ').toUpperCase()
      ),
      datasets: [{
        data: percentages.map(item => item.percentage),
        backgroundColor: [
          'rgba(59, 130, 246, 0.9)',
          'rgba(239, 68, 68, 0.9)',
        ],
        borderWidth: 2,
        borderColor: 'rgba(17, 24, 39, 0.8)'
      }]
    };
  };

  const chartOptions: ChartOptions = {
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: '#D1D5DB',
          padding: 20,
          font: {
            size: 14,
            weight: 'bold',
          },
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 6,
      },
      datalabels: {
        color: '#fff',
        font: {
          weight: 'bold',
          size: 14,
        },
        anchor: 'end',
        align: 'end',
        formatter: (value: number) => {
          return value.toFixed(1) + '%'
        },
        display: (context: ChartContext) => {
          const value = context.dataset.data[context.dataIndex] as number
          return value > 5
        },
      },
    },
    layout: {
      padding: {
        bottom: 20,
      },
    },
    radius: '85%',
  }

  ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels, DoughnutController)

  // Helper function to calculate return percentage
  const calculateReturn = (asset: AssetTotalDash) => {
    const invested = parseFloat(asset.total_value);
    if (invested === 0) return 0;
    
    const currentValue = parseFloat(asset.total_current_value);
    const dividends = asset.total_dividends ? parseFloat(asset.total_dividends) : 0;
    
    if (isNaN(invested) || isNaN(currentValue) || isNaN(dividends)) return 0;
    
    const totalReturn = ((currentValue + dividends) / invested - 1) * 100;
    return isFinite(totalReturn) ? totalReturn : 0;
  };

  const handleAssetClick = (assetType: string, currency: string) => {
    if (assetType === 'stocks') {
      navigate(`/investments/dashboard/stock?currency=${currency.toLowerCase()}`)
    }
    if (assetType === 'crypto') {
      navigate('/investments/dashboard/crypto')
    }
    if (assetType === 'tesouro-direto') {
      navigate('/investments/dashboard/tesouro')
    }
  }

  // Add loading state display
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-blue-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading investment data...</span>
          <span className="text-sm text-gray-400">If this takes too long, please refresh the page.</span>
        </div>
      </div>
    );
  }

  // Add empty state display
  if (!assetTotals || assetTotals.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <h2 className="text-xl font-semibold mb-2">No investment data available</h2>
          <p>Please add some investments to see your dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <TopBar 
        title="Investment Dashboard"
        subtitle="Track and analyze your investment portfolio"
      />
      {isRecalculating && (
        <div className="absolute top-[55px] right-[50px] bg-blue-500/20 px-3 py-1 rounded-md z-30 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
          <span className="text-blue-300 text-sm">Updating values...</span>
        </div>
      )}
      <div className="absolute top-[50px] right-[200px] flex items-center space-x-4 z-30">
        <span className="text-blue-400 font-semibold">
          1 USD = {Number(exchangeRate).toFixed(2)} BRL
        </span>
        <div className="flex items-center space-x-2">
          <span className="text-gray-300">BRL</span>
          <Switch
            checked={showUSD}
            onChange={setShowUSD}
            className={`${showUSD ? 'bg-blue-600' : 'bg-gray-600'}
              relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800`}
          >
            <span className={`${showUSD ? 'translate-x-6' : 'translate-x-1'}
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
          <span className="text-gray-300">USD</span>
        </div>
      </div>

      <main className="overflow-x-hidden overflow-y-auto relative z-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gray-800/30 backdrop-blur-sm shadow-lg overflow-hidden rounded-2xl border border-gray-700/50 mb-8"
          >
            <div className="px-6 py-8 sm:p-10">
              <h3 className="text-xl leading-6 font-semibold text-gray-300">Investments Value</h3>
              <div className="mt-4 text-5xl font-bold text-blue-400">
                {calculateTotalCurrentValue()}
              </div>
              <div className="mt-4 flex justify-between text-lg">
                <span className="text-gray-400">Total Invested:</span>
                <span className="text-gray-300">{calculateTotal()}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="text-gray-400">Total Dividends:</span>
                <span className="text-green-400">{calculateTotalDividends()}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="text-gray-400">Total Profit:</span>
                <span className={`${calculateTotalProfit() > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatTotalProfit()}
                </span>
              </div>
              <div className="mt-4 flex justify-between text-lg border-t border-gray-700 pt-4">
                <span className="text-gray-400">Total Return:</span>
                <span className={`${calculateTotalReturn() > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {calculateTotalReturn().toFixed(2)}%
                </span>
              </div>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {assetTotals.map((asset, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-gray-800/30 backdrop-blur-sm overflow-hidden shadow-lg rounded-xl border border-gray-700/50 cursor-pointer hover:bg-gray-800/40 transition-colors"
                onClick={() => handleAssetClick(asset.asset_type, asset.currency)}
              >
                <div className="px-6 py-5">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {asset.asset_type === 'stocks' && <LineChartIcon className="h-8 w-8 text-blue-400" />}
                      {asset.asset_type === 'crypto' && <CurrencyDollar className="h-8 w-8 text-green-400" />}
                      {asset.asset_type === 'tesouro-direto' && <ChartPie className="h-8 w-8 text-yellow-400" />}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-200 capitalize">
                        {asset.asset_type.replace('-', ' ')}
                      </h3>
                      <div className="text-sm text-gray-400">
                        {asset.currency.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-2xl font-bold text-blue-400">
                      {formatCurrency(asset.total_current_value, asset.currency)}
                    </div>
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-gray-400">Invested:</span>
                      <span className="text-gray-300">{formatCurrency(asset.total_value, asset.currency)}</span>
                    </div>
                    {/* Always render the dividends line but hide it for crypto */}
                    <div className={`flex justify-between text-sm ${asset.asset_type === 'crypto' ? 'invisible' : ''}`}>
                      <span className="text-gray-400">
                        {asset.asset_type === 'tesouro-direto' ? 'Interest Gains:' : 'Dividends:'}
                      </span>
                      <span className="text-green-400">
                        {formatCurrency(asset.total_dividends || '0', asset.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Profit:</span>
                      <span className={`${
                        Number(asset.total_current_value) - Number(asset.total_value) > 0 
                          ? 'text-green-400' 
                          : 'text-red-400'
                      }`}>
                        {formatCurrency(
                          (Number(asset.total_current_value) - Number(asset.total_value)).toString(), 
                          asset.currency
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-700">
                      <span className="text-gray-400">Return:</span>
                      <span className={`${calculateReturn(asset) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {calculateReturn(asset).toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
            <ChartCard title="Asset Type Allocation" data={generateChartData('asset')} options={chartOptions} />
            <ChartCard title="Currency Allocation" data={generateChartData('currency')} options={chartOptions} />
            <ChartCard title="Liquidity Allocation" data={generateLiquidityChartData()} options={chartOptions} />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

const ChartCard: React.FC<{ title: string; data: ChartData; options: ChartOptions }> = ({ title, data, options }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.6 }}
    className="bg-gray-800/30 backdrop-blur-sm shadow-lg overflow-hidden rounded-xl border border-gray-700/50"
  >
    <div className="px-6 py-6">
      <h3 className="text-xl font-semibold text-gray-200 mb-6">{title}</h3>
      <div className="h-[400px] flex justify-center items-center">
        <Pie data={data} options={options as ChartJSOptions<'pie'>} />
      </div>
    </div>
  </motion.div>
)

export default InvestmentDashboard